import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getCountries } from 'state/reducers/global.reducer';
import FormCreatorComponent, { isValidForm } from 'components/FormCreatorComponent';
import { createFormProps, fieldType } from 'components/FormCreatorComponent/types';
import { NewUserStatus, continueOptions, newAccountsSteps } from 'constants/new_accounts.constants';
import _ from 'lodash';
import { approveUserDetails, deleteNewUser, getExistContact, upsertUser } from 'services/new_accounts.service';
import {
	FormCard,
	FormCard1ColumnLayout,
	FormCard2ColumnLayout,
	FormCardLayout,
	FormCardSingleColumnLayout,
} from 'theme/elements/Forms';
import { ButtonComponent } from 'components/ButtonComponent';
import {
	getActiveStepIdx,
	getIsDirty,
	getIsLoading,
	getNewAccount,
	getNewUser,
	getNewUsers,
	initialNewUsers,
	setActiveStepIdx,
	setIsDirty,
	setNewUser,
	setNewUsers,
} from 'state/reducers/new_accounts.reducers';
import { newAccountTypesForms } from 'sections/NewAccounts/forms.types';
import { overrideNewAccountFormsToApprove } from 'sections/NewAccounts/forms.types.approve';
import { overrideNewAccountFormsToCreate } from 'sections/NewAccounts/forms.types.create';
import { birthCountryId, countryId } from 'constants/user.constants';
import { getHelperText, getValidation, prepareInitialValues } from '../Stepper/helper';
import { useParams } from 'react-router-dom';
import { FadeIn } from 'theme/elements/Content';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { setError } from 'state/reducers/pending-requests.reducer';
import { Stack } from '@mui/material';
import { getCompanies } from 'state/reducers/global.reducer';
import { WarningDialog } from 'components/WarningDialog';

export interface IUserDetails {
	handleBack?: any;
	handleForward?: any;
	isApproveMode?: boolean;
}

export const UserDetails = ({ handleBack, handleForward, isApproveMode }: IUserDetails) => {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const { action } = useParams();
	const newAccount = useAppSelector(getNewAccount);
	const newUsers = useAppSelector(getNewUsers);
	const newUser = useAppSelector(getNewUser);
	const countries = useAppSelector(getCountries);
	const activeStepIdx = useAppSelector(getActiveStepIdx);
	const isDirty = useAppSelector(getIsDirty);
	const isLoading = useAppSelector(getIsLoading);
	const [initialValues, setInitialValues] = useState<Record<string, any>>({});
	const [forms, setForms] = useState<createFormProps[]>([]);
	const [formState, setFormState] = useState<Record<string, any>>({});
	const [open, setOpen] = useState(false);
	const handleContinue = useRef(() => {});
	const [initialValidation, setInitialValidation] = useState<Record<string, any>>({});
	const initialNewUser = {
		id: 'new',
		firstNameHebrew: `*${t('page.accounts.new_account.user_details.add_user')}`,
	};
	const upsertNewUserPermission = hasPermission([permissionEnums.UPSERT_NEW_USER]);
	const approveUserDetailsPermission = hasPermission([permissionEnums.UPSERT_NEW_USER]);
	const companies = useAppSelector(getCompanies);

	const optionsDependies = useMemo(() => {
		const options: Record<string, any> = {};
		if (countries) {
			_.assign(options, {
				birthCountryId: countries.map((country: any) => {
					return { value: country.id, label: country.name };
				}),
			});
		}
		return options;
	}, [countries, initialValues]);

	const handleSaveUser = async (continueOption: continueOptions, skipEmailValidation?: boolean) => {
		let values = { ...initialValues };
		_.values(formState).forEach(
			(prop) =>
				(values =
					prop?.dirtyFields && !_.isEmpty(prop.dirtyFields) ? { ...values, ...prop.dirtyFields } : values)
		);
		if (_.has(newUser, 'id') && _.get(newUser, 'id') !== 'new') {
			_.assign(values, { id: newUser.id });
		}

		//check duplicate email
		if (
			_.find(
				_.values(newUsers),
				(user) => _.isEqual(user.emailAddress, values.emailAddress) && !_.isEqual(user.id, values?.id)
			)
		) {
			dispatch(
				setError({
					exist: true,
					text: _.replace(t('validations.errors.alreadyExist'), '{0}', t('label.email')),
				})
			);
			return false;
		}

		//check duplicate identificationNumber
		if (
			_.find(
				_.values(newUsers),
				(user) =>
					_.isEqual(user.identificationNumber, values.identificationNumber) && !_.isEqual(user.id, values?.id)
			)
		) {
			dispatch(
				setError({
					exist: true,
					text: _.replace(t('validations.errors.alreadyExist'), '{0}', t('label.identificationNumber')),
				})
			);
			return false;
		}

		const dateOfBirth = new Date(values.year, values.month, values.day, 12);
		const occupation = values.role;

		// eslint-disable-next-line prefer-const
		let newuserdata = _.clone(values);

		delete newuserdata.day;
		delete newuserdata.month;
		delete newuserdata.year;

		newuserdata.dateOfBirth = dateOfBirth;
		newuserdata.occupation = occupation;
		newuserdata.countryId = countryId;
		newuserdata.legalStatus = values.legalStatus;
		const exist = await getExistContact(newuserdata.emailAddress)
			.then((res) => {
				return res?.data?.user;
			})
			.catch(() => {});

		if (!_.isNil(exist) && !skipEmailValidation) {
			setOpen(true);
			handleContinue.current = () => {
				setOpen(false);
				handleSaveUser(continueOption, true);
			};
			return;
		}

		if (upsertNewUserPermission) {
			dispatch(
				upsertUser(
					{
						newAccountId: newAccount.id,
						newUsers: [newuserdata],
					},
					continueOption
				)
			);
		}
	};

	useEffect(() => {
		const values = prepareInitialValues(newUser);
		if (action !== 'review') {
			setForms([]);
		}
		setInitialValues(values);
		if (newUser?.status === NewUserStatus.DRAFT && isApproveMode && approveUserDetailsPermission) {
			dispatch(approveUserDetails(newUser.id, newAccount.id));
		}
		window.scrollTo(0, 0);
	}, [newUser]);

	useEffect(() => {
		if (_.isEmpty(newUsers)) {
			if (activeStepIdx === 2) {
				dispatch(setNewUsers({ new: initialNewUser }));
				dispatch(setNewUser(initialNewUser));
			}
		} else {
			if (_.isEmpty(newUser)) {
				const indexUser = isApproveMode ? 0 : _.values(newUsers).length - 1;
				dispatch(setNewUser(_.values(newUsers)[indexUser]));
			}
		}
	}, [newUsers]);

	useEffect(() => {
		if (initialValues) {
			const typeForms = _.get(newAccountTypesForms, newAccountsSteps.USER_DETAILS);
			let formsState: Record<string, any> = {};
			typeForms.forEach((form) => {
				const dirtyValues =
					_.has(formState, form.name) && _.has(formState[form.name], 'dirtyFields')
						? _.get(formState[form.name], 'dirtyFields')
						: {};
				formsState = {
					...formsState,
					[form.name]: isValidForm(form.fieldsGroups, { ...initialValues, ...dirtyValues }),
				};
			});
			setFormState(formsState);
		}
	}, [initialValues]);

	useEffect(() => {
		if (newUser.id !== 'new') {
			dispatch(
				setIsDirty({
					dirty: _.some(_.values(formState), { isDirty: true }),
					type: newAccountsSteps.USER_DETAILS,
				})
			);
		}
	}, [formState]);

	useEffect(() => {
		const basicForms = { ..._.get(newAccountTypesForms, newAccountsSteps.USER_DETAILS) };
		const override = isApproveMode
			? { ..._.get(overrideNewAccountFormsToApprove, newAccountsSteps.USER_DETAILS) }
			: { ..._.get(overrideNewAccountFormsToCreate, newAccountsSteps.USER_DETAILS) };
		const final = isApproveMode ? _.merge({}, override) : _.merge({}, basicForms, override);
		const formsArr = _.map(final, (form: createFormProps) => ({
			...form,
			fieldsGroups: _.map(form.fieldsGroups, (group) => ({
				...group,
				fields: _.map(group.fields, (field) => ({
					...field,
					props: {
						...field.props,
						helperText: getHelperText(newAccount, newAccountsSteps.USER_DETAILS, field, t),
						options:
							_.isEqual(field.type, fieldType.select) && _.has(optionsDependies, field.props.name)
								? _.get(optionsDependies, field.props.name)
								: field.props?.options || [],
					},
					validation: getValidation(newAccount, newAccountsSteps.USER_DETAILS, field),
				})),
			})),
		}));
		setForms([...formsArr]);
	}, [initialValues, optionsDependies]);

	const CardLayout = action === 'review' ? FormCardLayout : FormCardSingleColumnLayout;
	const ButtonsLayout = action === 'review' ? FormCard2ColumnLayout : FormCard1ColumnLayout;

	return (
		<FadeIn>
			<WarningDialog
				open={open}
				subTitle={t('validations.errors.emailAlreadyExistinAccount')}
				cancelLabel={t('button.cancel')}
				continueLabel={t('button.continue')}
				handleContinue={() => {
					setOpen(false);
				}}
				handleCancel={handleContinue.current}
			/>
			<CardLayout values={initialValues}>
				<>
					{!_.isEmpty(initialValues) &&
						forms &&
						forms?.map((form: any, index: number) => {
							return (
								<FormCard key={index} title={t(form.name)}>
									<FormCreatorComponent
										{...form}
										translation={t}
										values={initialValues}
										checkInitValidation={false}
										initialValidation={initialValidation}
										handleChange={(newFormState: Record<string, any>) => {
											setFormState({ ...formState, [form.name]: newFormState });
										}}
									/>
								</FormCard>
							);
						})}
				</>
			</CardLayout>
			<ButtonsLayout>
				<ButtonComponent
					disabled={isLoading || isDirty.dirty}
					handleClick={() => {
						dispatch(setActiveStepIdx(1));
					}}
					className="buttonForm"
					text={`< ${t('button.back')}`}
				/>
				{action !== 'review' && (
					<>
						<ButtonComponent
							disabled={isLoading || !isDirty.dirty}
							handleClick={() => {
								if (isDirty.dirty || _.some(_.values(formState), { isDirty: true })) {
									if (newUser.id === 'new') {
										dispatch(initialNewUsers(_.omit(newUsers, 'new')));
										if (!_.isEmpty(_.omit(newUsers, 'new'))) {
											dispatch(setNewUser(_.last(_.values(_.omit(newUsers, 'new')))));
										}
										//);
									} else {
										setForms([]);
										const values = prepareInitialValues(newUser);
										setInitialValues(values);
										dispatch(
											setIsDirty({
												dirty: false,
											})
										);
									}
								}
							}}
							text={`${newUser.id === 'new' ? t('button.cancel') : t('button.cancel_changes')}`}
							className="buttonForm"
						/>
						{newUser.id !== 'new' && _.keys(newUsers).length > 1 && (
							<ButtonComponent
								disabled={isLoading}
								handleClick={() => {
									dispatch(deleteNewUser({ id: newUser.id }));
								}}
								text={`${t('button.delete')}`}
								className="buttonForm"
							/>
						)}
						<ButtonComponent
							disabled={
								isLoading || isApproveMode ? true : !_.every(_.values(formState), { isValid: true })
							}
							handleClick={() => {
								isApproveMode ? handleForward() : handleSaveUser(continueOptions.SAVE_AND_NEW);
							}}
							text={`${t('label.save_and_add')}`}
							icon={
								<img
									src={'/icons/plus-sign.svg'}
									style={{
										marginLeft: '10px',
									}}
								/>
							}
							className="buttonForm"
						/>
					</>
				)}
				<Stack gap={2} flexDirection={'row'}>
					{isApproveMode && _.last(_.keys(newUsers)) !== _.toString(newUser?.id) && (
						<ButtonComponent
							disabled={isLoading}
							handleClick={() => {
								const nextId = _.indexOf(_.keys(newUsers), _.toString(newUser?.id)) + 1;
								dispatch(setNewUser({ ..._.values(newUsers)[nextId] }));
							}}
							text={`${t('label.to_reviewerBankAccount')}>`}
							className="buttonForm"
						/>
					)}
					<ButtonComponent
						disabled={
							isLoading || isApproveMode
								? !_.every(_.values(newUsers), { status: NewUserStatus.APPROVED })
								: !_.every(_.values(formState), { isValid: true })
						}
						handleClick={() =>
							isApproveMode ? handleForward() : handleSaveUser(continueOptions.SAVE_AND_CONTINUE)
						}
						text={`${t('button.to_next_step')} >`}
						className="buttonForm"
					/>
				</Stack>
			</ButtonsLayout>
		</FadeIn>
	);
};
